/**
 * POSTs JSON to an auth endpoint with the headers the server requires.
 *
 * `X-Requested-With` is what satisfies `csrfProtection` (server/middleware/
 * csrf.ts): it accepts a request carrying that header, and otherwise 403s
 * anything with an Origin. `apiRequest` in queryClient.ts sets it, but the
 * auth screens used bare `fetch()` and did not.
 *
 * The consequence was invisible in development, where the CSRF middleware
 * short-circuits for non-production, and invisible on /api/login and
 * /api/register, which are on the exempt list. Everything else the auth
 * screens call — MFA challenge dispatch, MFA verification, both WebAuthn
 * steps, and consent recording after registration — was hard-403'd in
 * production. Anyone with MFA enabled could not finish signing in.
 */
export async function authPost(url: string, body: unknown = {}): Promise<Response> {
  return fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'fetch',
    },
    body: JSON.stringify(body),
  });
}

/**
 * Pull a human-readable message out of an error response body.
 *
 * Server errors carry BOTH a machine code (`error`, e.g. "CSRF_PROTECTION",
 * "INVALID_CREDENTIALS") and a human sentence (`message`). The auth screens
 * read `error` and rendered it straight into the error banner, so users were
 * shown the literal string "CSRF_PROTECTION". Prefer `message`; fall back to
 * the caller's localized default. The raw code is never surfaced — it means
 * nothing to a user and hints at internals.
 */
export function authErrorMessage(body: unknown, fallback: string): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.trim().length > 0 ? message : fallback;
}

/**
 * Logs the current user out and returns them to the login screen.
 *
 * Hits the server logout endpoint (GET /api/logout — destroys the session
 * server-side for both the local and Replit auth providers) and then does a
 * client navigation to the SPA /login route. Call sites previously did
 * `window.location.href = '/api/logout'`, which navigated the browser
 * straight to the endpoint and left the user staring at its raw JSON
 * response ({"success":true,...}) instead of a login page.
 */
export async function logout(): Promise<void> {
  try {
    await fetch('/api/logout', { credentials: 'include' });
  } catch {
    // Network hiccup — still send them to /login. The session cookie is
    // httpOnly and is invalidated server-side on the next authed request.
  }
  window.location.href = '/login';
}
